// src/models/currentUserSlice.ts
import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import axios from 'axios';
import { RootState } from '../reduxStore/store';

interface Points {
  id: number;
  customer_id: number;
  current_points: number;
  redeemed_points: number;
  earned_points: number;
  created_at: string;
  updated_at: string;
  deleted_at: string | null;
}

interface Tier {
  title: string;
}

export interface User {
  id: number;
  first_name: string;
  last_name: string;
  email: string;
  mobile: string;
  email_verified_at: string | null;
  profile_picture: string | null;
  registered_date: string | null;
  is_active: number;
  dob: string | null;
  gender: string | null;
  source_id: string | null;
  created_at: string;
  updated_at: string;
  deleted_at: string | null;
  bearer_token: string;
  points: Points;
  tier: Tier;
}

interface CurrentUserState {
  user: User | null;
  loading: boolean;
  error: string | null;
}
export interface UpdateProfilePayload {
  first_name: string;
  last_name: string;
  mobile: string;
  dob: string | null;
}
const initialState: CurrentUserState = {
  user: null,
  loading: false,
  error: null,
};

interface ErrorResponse {
  message: string;
}

// Fetch current user
export const fetchCurrentUser = createAsyncThunk<User, void, { rejectValue: ErrorResponse, state: RootState }>(
  'currentUser/fetchCurrentUser',
  async (_, thunkAPI) => {
    const token = localStorage.getItem('accessToken');
    if (!token) return thunkAPI.rejectWithValue({ message: 'No token available' });

    try {
      const apiUrl = process.env.REACT_APP_API_URL;
      const response = await axios.get(`${apiUrl}/api/v1/auth/current-user`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      return response.data.data;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        return thunkAPI.rejectWithValue({ message: error.response.data.message || 'An error occurred' });
      }
      return thunkAPI.rejectWithValue({ message: 'An unknown error occurred' });
    }
  }
);

// Update user profile
// src/models/currentUserSlice.ts
export const updateUserProfile = createAsyncThunk<User, UpdateProfilePayload, { rejectValue: ErrorResponse, state: RootState }>(
  'currentUser/updateUserProfile',
  async (userData, thunkAPI) => {
    const token = localStorage.getItem('accessToken');
    if (!token) return thunkAPI.rejectWithValue({ message: 'No token available' });

    try {
      const apiUrl = process.env.REACT_APP_API_URL;
      const response = await axios.put(`${apiUrl}/api/v1/auth/update-profile`, {
        first_name: userData.first_name,
        last_name: userData.last_name,
        mobile: userData.mobile,
        dob: userData.dob
      }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      return response.data.data;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        return thunkAPI.rejectWithValue({ message: error.response.data.message || 'Update failed' });
      }
      return thunkAPI.rejectWithValue({ message: 'An unknown error occurred' });
    }
  }
);

const currentUserSlice = createSlice({
  name: 'currentUser',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchCurrentUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCurrentUser.fulfilled, (state, action: PayloadAction<User>) => {
        state.loading = false;
        state.user = action.payload;
      })
      .addCase(fetchCurrentUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message || 'Failed to fetch user';
      })
      .addCase(updateUserProfile.pending, (state) => {
        state.loading = true;
      })
      .addCase(updateUserProfile.fulfilled, (state, action) => {
        state.loading = false;
        if (state.user) {
          state.user = { ...state.user, ...action.payload };
        }
      })
      .addCase(updateUserProfile.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message || 'Update failed';
      });
  },
});

export default currentUserSlice.reducer;